
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MealKitIconButtonComponent } from '@yoni/mealcomponents-lib';
import { ModalRestaurantInfosComponent } from '../../Components/Modal/ModalRestaurantInfos/ModalRestaurantInfos.component';
import { EnvDescriptor } from 'app/Environment/EnvLoader';
import { ModalService } from '../../Services/modal.service';
import { Customer, QdmService, Restaurant } from '../../Services/qdm.service';
import { Subscription, combineLatest } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from 'app/Services/auth.service';
import { GestionService } from 'app/Services/gestion.service';

@Component({
    imports: [MealKitIconButtonComponent, FormsModule],
    selector: 'choose-restaurant-page',
    templateUrl: 'ChooseRestaurantPageComponent.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['ChooseRestaurantPageComponent.scss']
})
export class ChooseRestaurantPageComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private gestion = inject(GestionService);
  private qdmService = inject(QdmService);
  private router = inject(Router);
  private modal = inject(ModalService);
  private cdr = inject(ChangeDetectorRef);
  private title = inject(Title);
  private cookie = inject(CookieService);
  private auth = inject(AuthService);

  currentSearching: string = '';
  restaurants: Restaurant[] = [];
  allRestaurants: Restaurant[] = []; // Store all restaurants before filtering

  isDedicated: boolean = false;

  subscription: Subscription = new Subscription();
  needToInputCode: boolean = false;
  inputCode: string = '';
  errorMessage: string = '';

  loadingRestaurants: boolean = true;
  isVerifyingCode: boolean = true;

  customerUseCode: boolean = false;

  verifyInputCode() {
    this.isVerifyingCode = true;
    this.errorMessage = 'Vérification en cours...';
    const currentCustomer = this.qdmService.currentCustomerSubject.value;
    if (!currentCustomer) {
      this.isVerifyingCode = false;
      this.cdr.detectChanges();
      return;
    }

    if (!this.inputCode) {
      this.errorMessage = 'Veuillez entrer un code';
      setTimeout(() => (this.errorMessage = ''), 1000);
      this.isVerifyingCode = false;
      this.cdr.detectChanges();
      return;
    }

    this.errorMessage = 'Récupération des restaurants...';

    this.qdmService
      .fetchRestaurantsWithCode(this.inputCode, currentCustomer.customer_id)
      .then((restaurants) => {
        this.allRestaurants = restaurants.filter(
          (r) => r.cstID === currentCustomer.customer_id
        );

        // Apply enterprise filter if needed
        this.applyEnterpriseFilter();

        if (this.restaurants.length === 0) {
          this.errorMessage = 'Aucun restaurant avec ce code !';
          setTimeout(() => (this.errorMessage = ''), 1000);
        } else {
          this.selectRestaurantIfOnlyOne();
          this.needToInputCode = false;
          this.cookie.set(
            'code-' + currentCustomer.customer_id,
            this.inputCode,
            365,
            '/'
          );
        }

        this.isVerifyingCode = false;
        this.cdr.detectChanges();
      })
      .catch(() => {
        this.isVerifyingCode = false;
        this.cdr.detectChanges();
      });
  }

  // Apply enterprise filtering to restaurants
  applyEnterpriseFilter() {
    const enterprise = this.auth.userEnterpriseSubject.value;

    if (enterprise) {

      const allCompanies = enterprise.enterprises
        .map((e: any) => e.companies.map((c: any) => parseInt(c.reference)))
        .flat()
        .filter((c: any, index: any, self: any) => self.indexOf(c) === index);

      this.restaurants = this.allRestaurants.filter(r => allCompanies.includes(r.id) || !r.is_use_password);


    } else {
      // If no enterprise, show all restaurants
      this.restaurants = [...this.allRestaurants];
    }

    if(this.restaurants.length > 0){
      this.verifyBulkCompanyStatus();
    }
  }

  verifyBulkCompanyStatus() {
  // On ne demande le statut que pour les restos avec is_use_password = true
  const restaurantsToCheck = this.allRestaurants.filter(r => r.is_use_password);
  const restaurantsToCheckIds = restaurantsToCheck.map(r => r.id);

  this.gestion.fetchBulkCompanyStatus(restaurantsToCheckIds).then((statuses) => {
    const enabledIds = Object.keys(statuses).filter(key => statuses[parseInt(key)]);
    const disabledsIds = Object.keys(statuses).filter(key => !statuses[parseInt(key)]);

    this.restaurants = this.restaurants.filter(r =>
      !r.is_use_password || enabledIds.includes(r.id.toString())
    );

    this.cdr.detectChanges();
  });
}


  ngOnInit() {
    // Listen for auth changes to filter restaurants
    this.subscription.add(
      this.auth.userEnterprise$.subscribe((enterprise) => {
        this.applyEnterpriseFilter();
        this.selectRestaurantIfOnlyOne();
        this.cdr.detectChanges();
      })
    );

    this.subscription.add(
      this.qdmService.currentCustomer$.subscribe((customer) => {
        if (customer) {
          this.customerUseCode = customer.use_code || false;
          const cstId = customer.customer_id;
          const code = this.cookie.get('code-' + cstId);

          if (code) {
            this.inputCode = code;
            this.verifyInputCode();
          } else {
            this.isVerifyingCode = false;
          }
        }else{
          this.customerUseCode = false;
          this.cdr.detectChanges();
        }

      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.cookie.delete('goto', '/');
  }

  ngAfterViewInit() {
    if (this.qdmService.isPublicDomainOrPrivate() === 'public') {
      this.qdmService.listRestaurantsForCurrentDomain();
    } else {
      const subdomain = this.subdomain();
      this.qdmService.fetchAllCustomers().then((customers) => {
        const found = customers.find(
          (c) => (c.WEBAPP_LINK || '').split('.')[0] === subdomain
        );
        if (!found) {
          this.router.navigate(['/error/customer-not-found']);
          return;
        }

        const customer: Customer = {
          use_code: found.USE_CODE === '1',
          customer_id: found.customer_id,
          name: found.name,
          themeJson: found.THEME_JSON,
          webAppLink: found.WEBAPP_LINK,
          APP_NAME: found.APP_NAME,
        };

        this.qdmService.currentCustomerSubject.next(customer);

        if (!customer.use_code) {
          this.qdmService.listRestaurantsForCurrentDomain();
        }
      });
    }

    this.isDedicated = false;
    this.qdmService.selectRestaurant(undefined);

    this.subscription.add(
      this.qdmService.restaurants$.subscribe((data) => {
        this.allRestaurants = data;

        // Apply current enterprise filter
        this.applyEnterpriseFilter();

        if (this.cookie.check('goto')) {
          const goto = this.cookie.get('goto');
          const restaurant = data.find((r) => r.id === parseInt(goto));
          if (restaurant) {

            this.selectRestaurant(null, restaurant);
            this.router.navigate([`restaurant/${restaurant.id}`]);
            this.cookie.delete('goto', '/');
          }
        }

        this.loadingRestaurants = false;

        if (!this.needToInputCode) {
          this.selectRestaurantIfOnlyOne();
        }

        this.cdr.detectChanges();
      })
    );

    if (this.qdmService.isPublicDomainOrPrivate() === 'private') {
      this.isDedicated = true;
    } else {
      this.needToInputCode = false;
      this.inputCode = ' ';
    }

    this.cdr.detectChanges();

    this.subscription.add(
      this.qdmService.currentCustomer$.subscribe((customer) => {
        if (!customer) {
          this.title.setTitle(`${this.getEnv().title} - Établissements`);
          return;
        }

        this.needToInputCode = customer.use_code;

        this.title.setTitle(`${this.getEnv().title} - ${customer.name}`);
        this.cdr.detectChanges();
      })
    );
  }

  searchForRestaurant($event: KeyboardEvent) {
    this.currentSearching = ($event.target as any).value;
    console.log("searching", this.currentSearching);
  }

  getEnv(): EnvDescriptor {
    return (globalThis as any).env;
  }

  getRestaurantsFiltered(): Restaurant[] {
    return this.restaurants.filter((res) => {
      return res.name
        .toLowerCase()
        .includes(this.currentSearching.toLowerCase());
    });
  }

  subdomain(): string {
    return window.location.hostname.split('.')[0].toLocaleLowerCase();
  }

  selectRestaurant(ev: MouseEvent | null, restaurant: Restaurant) {
    if (ev === null) {
      // sélection depuis le selecteur direct
      this.qdmService.selectRestaurant(restaurant);
      if (restaurant) {
        this.router.navigate([`restaurant/${restaurant.id}`]);
      }
      return;
    }

    if (!ev.target) {
      return;
    }

    const target = ev.target as HTMLElement;

    // if target is child of a button, do not select restaurant
    if (target.closest('button')) {
      return;
    }

    this.qdmService.selectRestaurant(restaurant);

    if (restaurant !== null) {
      this.router.navigate([`restaurant/${restaurant.id}`]);
    } else {
      this.router.navigate(['']);
    }
  }

  genereateImageUrl(restaurant: Restaurant): string {
    if (restaurant.logo === 'nologo.png') {
      return `${(globalThis as any).env.qdmApiUrl}/upload/logos/nologo.png`;
    }
    return `${(globalThis as any).env.qdmApiUrl}/upload/logos/${
      restaurant.id
    }/${restaurant.logo}`;
  }

  showRestaurantInfos(res: Restaurant) {
    this.modal.open(
      ModalRestaurantInfosComponent,
      {
        restaurant: res,
      },
      () => {}
    );
  }

  protected readonly window = window;

  clearCode() {
    const currentCustomer = this.qdmService.currentCustomerSubject.value;
    if (!currentCustomer) return;

    this.cookie.delete('code-' + currentCustomer.customer_id, '/');
    this.router.navigate(['/']);
  }

  private selectRestaurantIfOnlyOne() {
    if (this.restaurants.length === 1) {
      this.qdmService.selectRestaurant(this.restaurants[0]);
      this.router.navigate(['restaurant', this.restaurants[0].id]);
      return;
    }
  }
}
